import React, { useCallback, useEffect, useState } from "react";
import { api, fmtDate, formatApiErrorDetail } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/Shared";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Plus, PaperPlaneTilt, CheckCircle, XCircle, Truck, PencilSimple, Trash, ArrowsClockwise, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";
import MaterialRequestSheet from "@/pages/procurement/MaterialRequestSheet";
import FulfilMRDialog from "@/pages/procurement/FulfilMRDialog";
import ReviewMRDialog from "@/pages/procurement/ReviewMRDialog";

const STATUS_STYLES = {
  draft:      "bg-slate-100 text-slate-700 border-slate-300",
  submitted:  "bg-amber-50 text-amber-800 border-amber-300",
  approved:   "bg-blue-50 text-blue-800 border-blue-300",
  fulfilled:  "bg-emerald-50 text-emerald-800 border-emerald-300",
  rejected:   "bg-red-50 text-red-700 border-red-300",
  cancelled:  "bg-slate-100 text-slate-500 border-slate-300 line-through",
};
const PRIORITY_STYLES = {
  low:    "bg-slate-100 text-slate-600",
  medium: "bg-blue-50 text-blue-700",
  high:   "bg-orange-50 text-orange-700",
  urgent: "bg-red-50 text-red-700 font-semibold",
};

function StatusPill({ status }) {
  return (
    <span className={`inline-flex items-center rounded-sm border px-1.5 py-[1px] text-[10px] font-semibold uppercase tracking-widest ${STATUS_STYLES[status] || STATUS_STYLES.draft}`}>
      {status}
    </span>
  );
}

export default function MaterialRequests({ projectId, embedded = false }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canRaise = isAdmin || user?.role === "manager";

  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [fulfilling, setFulfilling] = useState(null);
  const [reviewing, setReviewing] = useState(null);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (projectId) params.set("project_id", projectId);
    if (statusFilter !== "all") params.set("status", statusFilter);
    const { data } = await api.get(`/material-requests?${params.toString()}`);
    setRows(data || []);
  }, [projectId, statusFilter]);

  const loadProjects = useCallback(async () => {
    if (embedded) return;
    const { data } = await api.get("/projects");
    setProjects(data || []);
  }, [embedded]);

  useEffect(() => { load(); loadProjects(); }, [load, loadProjects]);

  // "N" keyboard shortcut — open create sheet
  useEffect(() => {
    const handler = () => canRaise && setCreating(true);
    window.addEventListener("celsius:new", handler);
    return () => window.removeEventListener("celsius:new", handler);
  }, [canRaise]);

  const projectName = (pid) => projects.find((p) => p.id === pid)?.name || (projectId ? "" : "—");

  const doAction = async (path, mrNo, verb, body = {}) => {
    if (!window.confirm(`${verb} MR ${mrNo}?`)) return;
    try {
      await api.post(path, body);
      toast.success(`${mrNo} ${verb.toLowerCase()}ed`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const del = async (mr) => {
    if (!window.confirm(`Delete ${mr.mr_no}?`)) return;
    try {
      await api.delete(`/material-requests/${mr.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div data-testid="material-requests-page">
      {!embedded && (
        <PageHeader
          title="Material Requests"
          subtitle="Raise requests for materials · route through approval → warehouse fulfilment → auto-stock-transfer to site"
        />
      )}

      {/* Action ribbon */}
      <div className="border-b border-slate-200 bg-white px-4 py-2 flex items-center gap-4 sticky top-0 z-10">
        {canRaise && (
          <button
            onClick={() => setCreating(true)}
            data-testid="new-mr-btn"
            className="flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-sm hover:bg-slate-50 transition-colors group"
          >
            <Plus size={18} weight="bold" className="text-[#EA580C]" />
            <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-700 group-hover:text-[#EA580C]">New MR</span>
          </button>
        )}
        <button
          onClick={load}
          className="flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-sm hover:bg-slate-50 transition-colors"
          data-testid="refresh-mr-btn"
        >
          <ArrowsClockwise size={18} className="text-slate-600" />
          <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-700">Refresh</span>
        </button>
        <div className="ml-auto flex items-center gap-2">
          <label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Filter</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="mr-status-filter"
            className="text-[12.5px] border border-slate-300 rounded-sm px-2 py-1 bg-white"
          >
            <option value="all">All</option>
            <option value="draft">Draft</option>
            <option value="submitted">Submitted</option>
            <option value="approved">Approved</option>
            <option value="fulfilled">Fulfilled</option>
            <option value="rejected">Rejected</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      <div className="p-6">
        {rows.length === 0 ? (
          <EmptyState
            title="No material requests"
            description={canRaise ? "Click 'New MR' above to raise your first request." : "You don't have permission to raise requests. Ask your manager."}
          />
        ) : (
          <div className="bc-card overflow-hidden" data-testid="mr-table">
            <Table className="bc-list">
              <TableHeader>
                <TableRow>
                  <TableHead>MR #</TableHead>
                  <TableHead>Date</TableHead>
                  {!embedded && <TableHead>Project</TableHead>}
                  <TableHead>Requested by</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead className="text-right">Items</TableHead>
                  <TableHead>Required by</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-56 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((mr) => {
                  const isOwner = mr.requested_by === user?.id;
                  const canEdit = (mr.status === "draft" || mr.status === "submitted") && (isOwner || isAdmin);
                  const canSubmit = mr.status === "draft" && (isOwner || isAdmin);
                  const canApprove = mr.status === "submitted" && isAdmin;
                  const canFulfil = mr.status === "approved" && (isAdmin || user?.role === "manager");
                  const canCancel = ["draft", "submitted", "approved"].includes(mr.status) && (isOwner || isAdmin);
                  const canDelete = isAdmin && !["approved", "fulfilled"].includes(mr.status);
                  return (
                    <TableRow key={mr.id} data-testid={`mr-row-${mr.id}`}>
                      <TableCell className="font-mono-num text-[12px] font-semibold text-[#EA580C]">{mr.mr_no}</TableCell>
                      <TableCell className="text-slate-600">{fmtDate(mr.created_at)}</TableCell>
                      {!embedded && <TableCell className="text-slate-700">{projectName(mr.project_id)}</TableCell>}
                      <TableCell className="text-slate-700">{mr.requested_by_name}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center rounded-sm px-1.5 py-[1px] text-[10px] uppercase tracking-widest ${PRIORITY_STYLES[mr.priority] || PRIORITY_STYLES.medium}`}>
                          {mr.priority}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-mono-num text-slate-600 text-[11.5px]">
                        {(() => {
                          const totalUnits = (mr.items || []).reduce((s, i) => s + Number(i.quantity || 0), 0);
                          return `${(mr.items || []).length} · ${totalUnits.toFixed(0)} units`;
                        })()}
                      </TableCell>
                      <TableCell className="text-slate-600 text-[12px]">{mr.required_by ? fmtDate(mr.required_by) : "—"}</TableCell>
                      <TableCell><StatusPill status={mr.status} /></TableCell>
                      <TableCell>
                        <div className="flex gap-1 justify-end">
                          {canSubmit && (
                            <button onClick={() => doAction(`/material-requests/${mr.id}/submit`, mr.mr_no, "Submit")} data-testid={`submit-mr-${mr.id}`} title="Submit for approval" className="text-[#EA580C] hover:text-[#c2410c] p-1"><PaperPlaneTilt size={13} /></button>
                          )}
                          {canApprove && (
                            <button onClick={() => setReviewing(mr)} data-testid={`review-mr-${mr.id}`} title="Review & approve" className="text-emerald-600 hover:text-emerald-800 p-1"><CheckCircle size={13} weight="fill" /></button>
                          )}
                          {!canApprove && (
                            <button onClick={() => setReviewing(mr)} data-testid={`view-mr-${mr.id}`} title="View details" className="text-slate-500 hover:text-[#EA580C] p-1"><Eye size={13} /></button>
                          )}
                          {canFulfil && (
                            <button onClick={() => setFulfilling(mr)} data-testid={`fulfil-mr-${mr.id}`} title="Fulfil (issue stock)" className="text-blue-600 hover:text-blue-800 p-1"><Truck size={13} weight="fill" /></button>
                          )}
                          {canEdit && (
                            <button onClick={() => setEditing(mr)} data-testid={`edit-mr-${mr.id}`} title="Edit" className="text-slate-500 hover:text-[#EA580C] p-1"><PencilSimple size={13} /></button>
                          )}
                          {canCancel && mr.status !== "cancelled" && (
                            <button onClick={() => doAction(`/material-requests/${mr.id}/cancel`, mr.mr_no, "Cancel")} data-testid={`cancel-mr-${mr.id}`} title="Cancel" className="text-slate-500 hover:text-red-600 p-1 text-[10.5px] uppercase font-semibold tracking-widest">Cxl</button>
                          )}
                          {canDelete && (
                            <button onClick={() => del(mr)} data-testid={`delete-mr-${mr.id}`} className="text-slate-400 hover:text-red-600 p-1"><Trash size={13} /></button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <MaterialRequestSheet
        open={creating}
        onOpenChange={setCreating}
        projects={projects}
        projectId={projectId}
        onSaved={load}
      />
      {editing && (
        <MaterialRequestSheet
          open={!!editing}
          onOpenChange={(v) => !v && setEditing(null)}
          projects={projects}
          projectId={projectId}
          existing={editing}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {fulfilling && (
        <FulfilMRDialog
          mr={fulfilling}
          onClose={() => setFulfilling(null)}
          onDone={() => { setFulfilling(null); load(); }}
        />
      )}
      {reviewing && (
        <ReviewMRDialog
          mr={reviewing}
          projects={projects}
          currentUser={user}
          onClose={() => setReviewing(null)}
          onDone={() => { setReviewing(null); load(); }}
        />
      )}
    </div>
  );
}
